import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';

import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { Subscription } from 'rxjs';
import { WeekDay } from 'src/app/model/week-day';
import { CustomValidators } from 'src/app/validators/custom.validators';
import * as moment from 'moment';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDynamicComponent, StandardNewValidator, StandardNewValidatorStepTabs, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { TranslateService } from '@ngx-translate/core';
import { MtmaTimeslice } from 'src/app/model/mtma-rules-access';

@Component({
	selector: 'app-mtma-time-slice-dialog',
	templateUrl: './mtma-time-slice-dialog.component.html',
	providers: [{
		provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true }
	},]
})

export class MtmaTimeSliceDialogComponent implements OnInit, OnDestroy {
	activeIndex: number = 0;

	dayMonthList: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];

	timeslice: MtmaTimeslice;
	editMode = false;

	timesliForm: FormGroup;

	editedSub: Subscription;
	addedSub: Subscription;
	deviceInfoSub: Subscription;
	mtmaTimesliceModifySub: Subscription;

	listFlag: number[];
	daysOfWeek: WeekDay[] = [
		{ Value: '0', Name: 'SUN' },
		{ Value: '1', Name: 'MON' },
		{ Value: '2', Name: 'TUE' },
		{ Value: '3', Name: 'WED' },
		{ Value: '4', Name: 'THU' },
		{ Value: '5', Name: 'FRI' },
		{ Value: '6', Name: 'SAT' },
	];

	// design
	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		private translate: TranslateService,
		public config: DynamicDialogConfig,
	) {
		this.timeslice = new MtmaTimeslice();
		if (config.data !== undefined && config.data.timeslice !== undefined) {
			this.editMode = true;
			this.timeslice = { ...config.data.timeslice };
			if (this.timeslice.Weekdays === 0) {
				this.timeslice.Weekdays = 127;
			}
		} else {
			const dtFrom = moment().utc();
			dtFrom.utc().set('hour', 0);
			dtFrom.utc().set('minute', 0);
			dtFrom.utc().set('date', 1);
			const dtTo = moment().utc();
			dtTo.utc().set('hour', 23);
			dtTo.utc().set('minute', 59);
			dtTo.utc().set('date', moment().daysInMonth());
			this.timeslice.Type = 1;
			this.timeslice.Flags = 4;
			this.timeslice.Weekdays = 127;
			this.timeslice.DtFrom = dtFrom.utc().format('yyyy-MM-DDTHH:mm');
			this.timeslice.DtTo = dtTo.utc().format('yyyy-MM-DDTHH:mm');
		}
	}

	ngOnInit(): void {
		this.listFlag = [];
		let value = this.timeslice.Flags;
		for (let i = 6; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				this.listFlag.push(1);
			} else {
				this.listFlag.push(0);
			}
			value = value % (2 ** i);
		}

		this.setupDetailsForm();
		this.editedSub = this.store.select('permission', 'edited').pipe(filter(ed => !!ed)).subscribe(() => {
			this.closeDialog();
		});
		this.addedSub = this.store.select('permission', 'added').pipe(filter(ed => !!ed)).subscribe(() => {
			this.closeDialog();
		});

		this.mtmaTimesliceModifySub = this.store.select('device', 'mtmaTimesliceModify')
			.pipe(filter(mtmaTimesliceModify => !!mtmaTimesliceModify))
			.subscribe(() => {
				this.resetClose();
			});

		this.onResize();
		StandardNewValidator.setFirstCompile(true);

		StandardNewValidatorStepTabs.firstTabfields = [];
		StandardNewValidatorStepTabs.secondTabfields = [];

		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.name, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.type, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.includeExclude, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.temporary, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.repetition, 0);

		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.weekDays, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.dayMonthStart, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.dayMonthEnd, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.startDate, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.endDate, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.startTime, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.endTime, 1);
	}

	ngOnDestroy(): void {
		this.editedSub?.unsubscribe();
		this.addedSub?.unsubscribe();
		this.deviceInfoSub?.unsubscribe();
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.resetTimeSliceForm());
		this.closeDialog();
	}

	setupDetailsForm(): void {
		/* ------ DETAILS --------------- */
		let repetition = 0;
		if (this.listFlag[6 - Flag.REPETITION_MONTHLY] === 1) {
			repetition = Flag.REPETITION_MONTHLY;
		} else if (this.listFlag[6 - Flag.REPETITION_ANNUAL] === 1) {
			repetition = Flag.REPETITION_ANNUAL;
		}
		let inEs = 0;
		if (this.listFlag[6 - Flag.ESCLUDE_INCLUDE] === 1) {
			inEs = 1;
		}
		let temporary = 0;
		if (this.listFlag[6 - Flag.TEMPORARY] === 1) {
			temporary = 1;
		}
		/* ------ TIMESLICE --------------- */

		const currentWeekDays = [];
		let value = this.timeslice.Weekdays;
		for (let i = 6; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				currentWeekDays.unshift(i.toString());
			}
			value = value % (2 ** i);
		}
		let dayMonthStart = moment(this.timeslice.DtFrom).date();
		let dayMonthEnd = moment(this.timeslice.DtTo).date();
		if (this.listFlag[6 - Flag.DATE_29] + this.listFlag[6 - Flag.DATE_30] + this.listFlag[6 - Flag.DATE_31] > 0) {
			if (this.listFlag[6 - Flag.DATE_31] === 1) {
				dayMonthEnd = 31;
			}
			if (this.listFlag[6 - Flag.DATE_30] === 1 && this.listFlag[6 - Flag.DATE_31] === 1) {
				dayMonthStart = 30;
			} else if (this.listFlag[6 - Flag.DATE_30] === 1 && this.listFlag[6 - Flag.DATE_31] === 0) {
				dayMonthEnd = 30;
			}
			if (this.listFlag[6 - Flag.DATE_29] === 1 && (this.listFlag[6 - Flag.DATE_31] === 1 || this.listFlag[6 - Flag.DATE_30] === 1)) {
				dayMonthStart = 29;
			} else if (this.listFlag[6 - Flag.DATE_30] === 1 && this.listFlag[6 - Flag.DATE_31] === 0 && this.listFlag[6 - Flag.DATE_30] === 0) {
				dayMonthEnd = 29;
			}
		}
		const wDayArr = this.daysOfWeek.map((weekday) => this.fb.control(currentWeekDays.includes(weekday.Value)));
		/* ------FORM --------------- */
		this.timesliForm = new FormGroup({
			'name': new FormControl(this.timeslice.Name, [Validators.required, Validators.maxLength(48)]),
			'type': new FormControl(this.timeslice.Type, [Validators.required]),
			'repetition': new FormControl(repetition.toString(), [Validators.required]),
			'includeExclude': new FormControl(inEs.toString(), [Validators.required]),
			'temporary': new FormControl(temporary.toString(), [Validators.required]),
			'weekDays': this.fb.array(wDayArr, CustomValidators.multipleCheckboxRequireOne),
			'startDate': new FormControl(moment(this.timeslice.DtFrom.substring(0, this.timeslice.DtFrom.indexOf('T'))).toDate()),
			'startTime': new FormControl(this.getTime(this.timeslice.DtFrom), [Validators.required]),
			'endDate': new FormControl(moment(this.timeslice.DtTo.substring(0, this.timeslice.DtTo.indexOf('T'))).toDate()),
			'endTime': new FormControl(this.getTime(this.timeslice.DtTo), [Validators.required]),
			'dayMonthStart': new FormControl(dayMonthStart),
			'dayMonthEnd': new FormControl(dayMonthEnd),
		});
		this.timesliForm.addValidators(CustomValidators.checkCorrectDateSequence('startDate', 'startTime', 'endDate', 'endTime'));
		this.timesliForm.addValidators(CustomValidators.checkMinor('dayMonthStart', 'dayMonthEnd'));
		this.timesliForm.addValidators(CustomValidators.checkCorrectTimeSequence('startTime', 'endTime'));
	}

	canSwitchTab(): boolean {
		return StandardNewValidatorStepTabs.canSwitchTab(this.activeIndex);
	}

	changeActiveTabClick(index: number) {
		// if (this.canSwitchTab()) {
		this.activeIndex = index;
	}

	getErrorMessage(type: MtmaTimeSliceDialogComponent["f"]): string {
		let keyError = Object.keys(type.errors).toString();

		switch (keyError) {
			case "tooEarly":
				return this.translate.instant("TAG_DATE_WARNING_INVALID");
			default:
				return this.translate.instant("REQUIRED");
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.timesliForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.timesliForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save() {
		const timeslice = { ...this.timeslice };
		timeslice.Name = this.f.name.value;
		timeslice.Type = parseInt(this.f.type.value);
		let valueFlag = 0;
		if (parseInt(this.f.repetition.value) !== 0 && timeslice.Type === 2) {
			valueFlag = 2 ** this.f.repetition.value;
		}
		if (parseInt(this.f.includeExclude.value) !== 0) {
			valueFlag = valueFlag + 1;
		}
		if (parseInt(this.f.temporary.value) === 1) {
			valueFlag = valueFlag + 2 ** Flag.TEMPORARY;
		}
		if (timeslice.Type === 1) {
			timeslice.Weekdays = this.parseDays();
		} else {
			timeslice.Weekdays = 0;
		}

		let startTimeStr = this.f.startTime.value.toString().toLowerCase()
		if (!/^\d$/.test(startTimeStr.charAt(0))) {
			// non è in formato numerico
			startTimeStr = startTimeStr.substring(16, 21)
			this.f.startTime.value = startTimeStr;
		}

		let endTimeStr = this.f.endTime.value.toString().toLowerCase()
		if (!/^\d$/.test(endTimeStr.charAt(0))) {
			// non è in formato numerico
			endTimeStr = endTimeStr.substring(16, 21)
			this.f.endTime.value = endTimeStr;
		}

		const momTimeFrom = moment().local()
			.hours(+this.f.startTime.value.split(':')[0])
			.minutes(+this.f.startTime.value.split(':')[1]);
		const momTimeTo = moment().local()
			.hours(+this.f.endTime.value.split(':')[0])
			.minutes(+this.f.endTime.value.split(':')[1]);
		const momDateFrom = moment(this.f.startDate.value);
		const momDateTo = moment(this.f.endDate.value);

		//DATE VAL

		if (timeslice.Type === 2 && parseInt(this.f.repetition.value) === Flag.REPETITION_MONTHLY) {
			// PERIODO SPECIALE e RIPETIZIONE MENSILE
			//Set bitmask Flag, 29-30-31
			if (this.f.dayMonthStart.value > 28) {
				switch (this.f.dayMonthStart.value) {
					case 29:
						valueFlag = valueFlag + 2 ** Flag.DATE_29;
						break;
					case 30:
						valueFlag = valueFlag + 2 ** Flag.DATE_30;
						break;
					case 31:
						valueFlag = valueFlag + 2 ** Flag.DATE_31;
						break;
				}
			}
			if (momDateFrom.daysInMonth() >= this.f.dayMonthStart.value) {
				momDateFrom.set('date', this.f.dayMonthStart.value);
			} else {
				momDateFrom.set('date', momDateFrom.daysInMonth());
			}
			//Set bitmask Flag, 29-30-31
			if (this.f.dayMonthEnd.value > 28) {
				switch (this.f.dayMonthEnd.value) {
					case 29:
						valueFlag = valueFlag + 2 ** Flag.DATE_29;
						break;
					case 30:
						valueFlag = valueFlag + 2 ** Flag.DATE_30;
						break;
					case 31:
						valueFlag = valueFlag + 2 ** Flag.DATE_31;
						break;
				}
			}
			if (momDateTo.daysInMonth() >= this.f.dayMonthEnd.value) {
				momDateTo.set('date', this.f.dayMonthEnd.value);
			} else {
				momDateTo.set('date', momDateTo.daysInMonth());
			}
		}
		timeslice.Flags = valueFlag;

		//DATE VAL
		timeslice.DtFrom = momDateFrom.format('yyyy-MM-DD') + momTimeFrom.format('THH:mm');
		timeslice.DtTo = momDateTo.format('yyyy-MM-DD') + momTimeTo.format('THH:mm');
		if (timeslice.Id) {
			this.store.dispatch(DeviceActions.editMtmaTimeSlice({ timeslice }));
		} else {
			this.store.dispatch(DeviceActions.addMtmaTimeSlice({ timeslice }));
		}
	}

	getTime(date: string): string {
		return moment(date).format('HH:mm');
	}

	parseDays(): number {
		const WDControl = this.f.weekDays as FormArray;
		const days = [];
		let value = 0;
		WDControl.controls.forEach((c, i) => {
			if (c.value) {
				days.push(i);
				value = value + 2 ** i;
			}
		});
		return value;
	}

	isSpecialPeriod(): boolean {
		if (parseInt(this.f.type.value) === 2) {
			return true;
		}
		return false;
	}

	isRepetitionAnnual(): boolean {
		if (parseInt(this.f.repetition.value) === Flag.REPETITION_ANNUAL) {
			return true;
		}
		return false;
	}

	isTemporary(): boolean {
		if (parseInt(this.f.temporary.value) === 1) {
			return true;
		}
		return false;
	}

	endTimeMarginBottom(): string {
		if (this.isTemporary()) {
			return ""
		}
		if (this.isSpecialPeriod()) {
			return "mb-6 pb-4";
		}
		return "mb-8 pb-11";
	}

	get f(): any {
		return this.timesliForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	enableConfirmButtons(): boolean {
		return this.activeIndex > 0;
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	overTablet(): boolean {
		return StandardPageLayout.overTablet(this.screenWidth);
	}

	col6Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-20rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	showErrorConfirmInvalidMessage(type: MtmaTimeSliceDialogComponent["f"], notEquivalentType: string): boolean {
		return StandardNewValidator.showErrorConfirmInvalidMessage(type, notEquivalentType, this.timesliForm);
	}

	newShowErrors(type: MtmaTimeSliceDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	getColorFirstTab(): string {
		this.canSwitchTab();
		return StandardNewValidatorStepTabs.getColorFirstTab();
	}

	getColorSecondTab(): string {
		return StandardNewValidatorStepTabs.getColorSecondTab(this.activeIndex);
	}

	getColorThirdTab(): string {
		return StandardNewValidatorStepTabs.getColorThirdTab(this.activeIndex);
	}

	showErrorRequiredMessage(type: MtmaTimeSliceDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	manageRespStyle(classType: string): string {
		return StandardPageLayout.manageRespStyle(classType, this.screenWidth);
	}

	manageRespStylewithError(type: MtmaTimeSliceDialogComponent["f"], classType: string): string {
		return StandardNewValidator.manageRespStylewithError(type, classType, this.screenWidth);
	}

	showErrorRequiredCheckBox(type: MtmaTimeSliceDialogComponent["f"]): string {
		return StandardNewValidator.showErrorRequiredCheckBox(type);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
}

enum Flag {
	DATE_31 = 6,
	DATE_30 = 5,
	DATE_29 = 4,
	REPETITION_ANNUAL = 3,
	REPETITION_MONTHLY = 2,
	TEMPORARY = 1,
	ESCLUDE_INCLUDE = 0
}
